import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  ContactFormValues,
  useContactForm,
} from "@/hooks/forms/entities/use-contact-form";
import { VendorSource } from "@/core";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

type ContactFormSchema = z.ZodType<ContactFormValues, unknown>;

interface ContactDialogProps {
  contact?: Partial<ContactFormValues> | null;
  description: string;
  entityLabel: string;
  mode: "create" | "edit";
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: ContactFormValues) => Promise<void>;
  open: boolean;
  schema: ContactFormSchema;
  submittingText?: string;
  isSubmitting?: boolean;
  submitText: string;
  title: string;
  variant?: "default" | "vendor" | "client";
}

export function ContactDialog({
  contact,
  description,
  entityLabel,
  mode,
  onOpenChange,
  onSubmit,
  open,
  schema,
  submittingText,
  isSubmitting = false,
  submitText,
  title,
  variant = "default",
}: Readonly<ContactDialogProps>) {
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const form = useContactForm({
    mode,
    contact,
    schema,
  });

  const handleCloseRequest = (nextOpen: boolean) => {
    if (isSubmitting) {
      return;
    }

    if (nextOpen) {
      onOpenChange(true);
      return;
    }

    if (form.formState.isDirty) {
      setShowCloseConfirmation(true);
      return;
    }

    form.reset();
    onOpenChange(false);
  };

  const handleConfirmClose = () => {
    form.reset();
    setShowCloseConfirmation(false);
    onOpenChange(false);
  };

  const handleCancelClose = () => {
    setShowCloseConfirmation(false);
  };

  const handleSubmit = async (data: ContactFormValues) => {
    await onSubmit(data);
    form.reset();
  };

  return (
    <>
      <Sheet open={open} onOpenChange={handleCloseRequest}>
        <SheetContent
          side="right"
          className="w-full max-w-full overflow-y-auto p-4 sm:max-w-xl sm:p-8"
        >
          <SheetHeader className="p-0">
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>{description}</SheetDescription>
          </SheetHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="mt-6 space-y-4"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First name</FormLabel>
                      <FormControl>
                        <Input placeholder="First name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last name</FormLabel>
                      <FormControl>
                        <Input placeholder="Last name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="name@example.com" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input placeholder="Phone number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {variant === "vendor" || variant === "client" ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="companyName"
                    render={({ field }) => (
                      <FormItem className="sm:col-span-2">
                        <FormLabel>Company name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Company name" {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {variant === "client" ? (
                    <FormField
                      control={form.control}
                      name="type"
                      render={({ field }) => (
                        <FormItem className="sm:col-span-2">
                          <FormLabel>Client type</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value ?? ""}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select client type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="travel agent">Travel agent</SelectItem>
                              <SelectItem value="broker">Broker</SelectItem>
                              <SelectItem value="private">Private</SelectItem>
                              <SelectItem value="family">Family</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ) : null}
                  {variant === "vendor" ? (
                    <FormField
                      control={form.control}
                      name="source"
                      render={({ field }) => (
                        <FormItem className="sm:col-span-2">
                          <FormLabel>Vendor source</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value ?? ""}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select vendor source" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value={VendorSource.CRM}>CRM</SelectItem>
                              <SelectItem value={VendorSource.BROKER}>Broker</SelectItem>
                              <SelectItem value={VendorSource.FRIEND}>Friend</SelectItem>
                              <SelectItem value={VendorSource.FAMILY}>Family</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ) : null}
                  <FormField
                    control={form.control}
                    name="secondaryEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Secondary email</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="backup@example.com" type="email" {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="secondaryPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Secondary phone</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Secondary phone number"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              ) : null}

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleCloseRequest(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {submittingText || "Saving..."}
                    </>
                  ) : (
                    submitText
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={showCloseConfirmation}
        onOpenChange={setShowCloseConfirmation}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Close {entityLabel} {mode === "create" ? "creation" : "edit"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Unsaved changes will be lost if you close now.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>
              Continue editing
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose}>
              Close
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
